import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import QuotationService from '../../services/QuotationService';
import QuotationReceipt from '../../components/Quotation/QuotationReceipt';

const statusOptions = [
    { label: 'Hepsi', value: null },
    { label: 'Taslak', value: 1 },
    { label: 'Gönderildi', value: 2 },
    { label: 'Kabul Edildi', value: 3 },
    { label: 'Reddedildi', value: 4 },
    { label: 'Süresi Doldu', value: 5 },
    { label: 'Satışa Çevrildi', value: 6 }
];

export default function QuotationListPage() {
    const [quotations, setQuotations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        status: null,
        customerId: null
    });
    const toast = useRef(null);
    const receiptRef = useRef(null);
    const navigate = useNavigate();

    const loadQuotations = async () => {
        setLoading(true);
        try {
            const res = await QuotationService.getList(filters);
            if (res.success) {
                setQuotations(res.data);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Teklifler yüklenirken hata oluştu.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadQuotations();
    }, [filters]);

    const getStatusSeverity = (status) => {
        switch (status) {
            case 1: return 'info'; // Draft
            case 2: return 'warning'; // Sent
            case 3: return 'success'; // Accepted
            case 4: return 'danger'; // Rejected
            case 5: return 'secondary'; // Expired
            case 6: return 'success'; // Converted
            default: return null;
        }
    };

    const getStatusLabel = (status) => {
        return statusOptions.find(o => o.value === status)?.label || 'Bilinmiyor';
    };

    const statusTemplate = (rowData) => {
        return <Tag value={getStatusLabel(rowData.status)} severity={getStatusSeverity(rowData.status)} />;
    };

    const confirmConversion = (id) => {
        confirmDialog({
            message: 'Bu teklifi gerçek bir satışa dönüştürmek istediğinize emin misiniz? Bu işlem stokları düşürecek ve müşteriyi borçlandıracaktır.',
            header: 'Satışa Dönüştür',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Evet, Dönüştür',
            rejectLabel: 'Vazgeç',
            acceptClassName: 'p-button-success',
            accept: () => handleConvertToSale(id)
        });
    };

    const handleConvertToSale = async (id) => {
        try {
            setLoading(true);
            const res = await QuotationService.convertToSale(id);
            if (res.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Teklif başarıyla satışa dönüştürüldü.' });
                loadQuotations();
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Dönüştürme işlemi başarısız.' });
        } finally {
            setLoading(false);
        }
    };

    const actionTemplate = (rowData) => {
        const isDraft = rowData.status === 1;
        const isConverted = rowData.status === 6;
        return (
            <div className="flex gap-2">
                <Button icon="pi pi-pencil" rounded text severity="info"
                    disabled={!isDraft}
                    onClick={() => navigate(`/quotations/edit/${rowData.id}`)}
                    tooltip="Düzenle" tooltipOptions={{ position: 'top' }} />

                <Button icon="pi pi-sync" rounded text severity="success"
                    disabled={isConverted}
                    onClick={() => confirmConversion(rowData.id)}
                    tooltip="Satışa Çevir" tooltipOptions={{ position: 'top' }} />
                <Button icon="pi pi-file-pdf" rounded text severity="danger"
                    onClick={() => receiptRef.current.downloadPDF(rowData.id)}
                    tooltip="PDF İndir" tooltipOptions={{ position: 'top' }} />

                <Dropdown value={rowData.status} options={statusOptions.filter(o => o.value !== null && o.value !== 6)}
                    disabled={isConverted}
                    onChange={(e) => updateStatus(rowData.id, e.value)}
                    className="status-dropdown"
                    placeholder="Durum"
                    style={{ width: '150px' }} />
            </div>
        );
    };

    const updateStatus = async (id, status) => {
        try {
            const res = await QuotationService.updateStatus(id, status);
            if (res.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Durum güncellendi.' });
                loadQuotations();
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Durum güncellenirken hata oluştu.' });
        }
    };

    return (
        <div className=" bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[3rem] shadow-2xl shadow-indigo-400/20 dark:shadow-none animate-fade-in max-w-8xl mx-auto space-y-6">
            <Toast ref={toast} />
            <ConfirmDialog />

            <style>{`
                .dark .p-datatable {
                    background: #0f172a !important;
                    color: #f8fafc !important;
                }
                .dark .p-datatable-header,
                .dark .p-datatable-thead > tr > th,
                .dark .p-datatable-tbody > tr {
                    background: #0f172a !important;
                    color: #f8fafc !important;
                    border-color: #1e293b !important;
                }
                .dark .p-datatable-tbody > tr.p-highlight {
                    background: #1e293b !important;
                    color: #38bdf8 !important;
                }
                .dark .p-datatable-tbody > tr:hover {
                    background: #1e293b/50 !important;
                }
                .dark .p-paginator {
                    background: #0f172a !important;
                    color: #f8fafc !important;
                    border-color: #1e293b !important;
                }
                .dark .p-paginator .p-paginator-first,
                .dark .p-paginator .p-paginator-prev,
                .dark .p-paginator .p-paginator-pages .p-paginator-page,
                .dark .p-paginator .p-paginator-next,
                .dark .p-paginator .p-paginator-last {
                    color: #cbd5e1 !important;
                }
                .dark .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
                    background: #1e293b !important;
                    color: #38bdf8 !important;
                }
                .dark .p-datatable-loading-overlay {
                    background: rgba(15, 23, 42, 0.7) !important;
                }
                .dark .p-calendar .p-inputtext,
                .dark .p-dropdown {
                    background: #1e293b !important;
                    border-color: #334155 !important;
                    color: white !important;
                }
                .dark .p-dropdown-panel {
                    background: #0f172a !important;
                    border: 1px solid #1e293b !important;
                }
                .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
                    color: #cbd5e1 !important;
                }
                .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item:hover,
                .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
                    background: #1e293b !important;
                    color: #38bdf8 !important;
                }
                .dark .p-datepicker {
                    background: #0f172a !important;
                    border: 1px solid #1e293b !important;
                    color: white !important;
                }
                .dark .p-datepicker table th,
                .dark .p-datepicker table td {
                    color: #cbd5e1 !important;
                }
                .dark .p-datepicker table td > span:hover {
                    background: #1e293b !important;
                }
                .dark .p-datepicker table td > span.p-highlight {
                    background: #38bdf8 !important;
                    color: #0f172a !important;
                }
                .dark .p-datepicker .p-datepicker-header {
                    background: #0f172a !important;
                    border-bottom: 1px solid #1e293b !important;
                    color: white !important;
                }
            `}</style>

            {/* Premium Page Header */}
            <div className="flex flex-col md:flex-row items-center justify-between mb-12 gap-6 p-6 bg-slate-900 dark:bg-slate-950 rounded-[2.5rem] text-white border border-slate-800 shadow-2xl">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2.5rem] bg-indigo-600 flex items-center justify-center shadow-2xl border-4 border-slate-700">
                        <i className="pi pi-file-edit text-white text-3xl"></i>
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-white leading-none uppercase tracking-tighter">
                            Tekliflerim
                        </h1>
                        <p className="text-slate-400 font-bold text-xs uppercase tracking-[0.2em] mt-3 italic">
                            Hazırlanan teklifleri buradan yönetebilirsiniz.
                        </p>
                    </div>
                </div>
                <Button
                    label="YENİ TEKLİF OLUŞTUR"
                    icon="pi pi-plus"
                    className="!bg-emerald-600 hover:!bg-emerald-700 border-none rounded-3xl px-6 h-[64px] font-black text-xs text-white shadow-xl transition-all hover:scale-[1.02]"
                    onClick={() => navigate('/quotations/add')}
                />
            </div>

            {/* Filter Bar */}
            <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-3xl border border-slate-100 dark:border-slate-700 flex flex-wrap gap-4 items-end">
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">Başlangıç</label>
                    <Calendar value={filters.startDate} onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.value }))} showIcon dateFormat="dd.mm.yy" className="w-40" />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">Bitiş</label>
                    <Calendar value={filters.endDate} onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.value }))} showIcon dateFormat="dd.mm.yy" className="w-40" />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">Durum</label>
                    <Dropdown value={filters.status} options={statusOptions} onChange={(e) => setFilters(prev => ({ ...prev, status: e.value }))} className="w-48" />
                </div>
                <Button icon="pi pi-filter-slash" className="p-button-secondary p-button-outlined rounded-xl" onClick={() => setFilters({ startDate: null, endDate: null, status: null, customerId: null })} tooltip="Filtreleri Temizle" />
            </div>

            {/* Table Section */}
            <div className="bg-white dark:bg-slate-900 shadow-2xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-800 rounded-[3rem] overflow-hidden">
                <DataTable value={quotations} loading={loading} paginator rows={10} className="p-datatable-sm"
                    emptyMessage="Teklif bulunamadı." responsiveLayout="stack" breakpoint="960px">
                    <Column field="quotationNo" header="TEKLİF NO" sortable className="p-4 font-bold text-slate-800 dark:text-slate-200" headerClassName="bg-transparent text-slate-700 dark:text-slate-300 font-black text-[14px] uppercase p-3 tracking-widest" />
                    <Column field="quotationDate" header="TARİH" body={(row) => new Date(row.quotationDate).toLocaleDateString('tr-TR')} sortable className="p-4 text-slate-700 dark:text-slate-300" headerClassName="bg-transparent text-slate-700 dark:text-slate-300 font-black text-[14px] uppercase p-3 tracking-widest" />
                    <Column field="customerName" header="MÜŞTERİ" sortable className="p-4 text-slate-700 dark:text-slate-300" headerClassName="bg-transparent text-slate-700 dark:text-slate-300 font-black text-[14px] uppercase p-3 tracking-widest" />
                    <Column field="grandTotal" header="TOPLAM TUTAR" body={(row) => row.grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺'} sortable className="p-4 font-black text-slate-900 dark:text-white" headerClassName="bg-transparent text-slate-700 dark:text-slate-300 font-black text-[14px] uppercase p-3 tracking-widest" />
                    <Column field="status" header="DURUM" body={statusTemplate} sortable className="p-4" headerClassName="bg-transparent text-slate-700 dark:text-slate-300 font-black text-[14px] uppercase p-3 tracking-widest" />
                    <Column field="validUntil" header="GEÇERLİLİK" body={(row) => row.validUntil ? new Date(row.validUntil).toLocaleDateString('tr-TR') : '-'} className="p-4 text-slate-700 dark:text-slate-300" headerClassName="bg-transparent text-slate-700 dark:text-slate-300 font-black text-[14px] uppercase p-3 tracking-widest" />
                    <Column header="İŞLEMLER" body={actionTemplate} style={{ width: '350px' }} className="p-4" headerClassName="bg-transparent text-slate-700 dark:text-slate-300 font-black text-[14px] uppercase p-3 tracking-widest" />
                </DataTable>
            </div>

            <QuotationReceipt ref={receiptRef} />
        </div>
    );
}
